import React from 'react';

// South Indian Square Style Kundli Chart
const SouthIndianChart = ({ 
  planets = {}, // { planetName: { house: 1, degree: 25.5, retrograde: false, sign: 'Leo' } }
  houseSignMap = {}, // { 1: 'Leo', 2: 'Virgo', ... }
  ascendantSign = 'Leo',
  size = 320,
  title = '',
  showDegrees = false
}) => {
  
  // Planet abbreviations
  const planetAbbrev = {
    'Sun': 'Su', 'Moon': 'Mo', 'Mars': 'Ma', 'Mercury': 'Me',
    'Jupiter': 'Ju', 'Venus': 'Ve', 'Saturn': 'Sa', 'Rahu': 'Ra', 'Ketu': 'Ke',
    'Ascendant': 'As', 'Lagna': 'Lg'
  };

  // Signs in order (fixed positions in South Indian chart)
  const signs = [
    'Pisces', 'Aries', 'Taurus', 'Gemini',
    'Aquarius', null, null, 'Cancer',
    'Capricorn', null, null, 'Leo',
    'Sagittarius', 'Scorpio', 'Libra', 'Virgo'
  ];

  // Sign abbreviations
  const signAbbrev = {
    'Aries': 'Ar', 'Taurus': 'Ta', 'Gemini': 'Ge', 'Cancer': 'Ca',
    'Leo': 'Le', 'Virgo': 'Vi', 'Libra': 'Li', 'Scorpio': 'Sc',
    'Sagittarius': 'Sg', 'Capricorn': 'Cp', 'Aquarius': 'Aq', 'Pisces': 'Pi'
  };

  // Planet colors
  const planetColors = {
    'Sun': '#F59E0B', 'Moon': '#E5E7EB', 'Mars': '#EF4444', 'Mercury': '#22C55E',
    'Jupiter': '#EAB308', 'Venus': '#EC4899', 'Saturn': '#3B82F6', 'Rahu': '#8B5CF6', 'Ketu': '#D97706'
  };

  // Sign to index mapping
  const signToIndex = {
    'Aries': 0, 'Taurus': 1, 'Gemini': 2, 'Cancer': 3,
    'Leo': 4, 'Virgo': 5, 'Libra': 6, 'Scorpio': 7,
    'Sagittarius': 8, 'Capricorn': 9, 'Aquarius': 10, 'Pisces': 11
  };

  const scale = size / 400;
  const cellSize = 100 * scale;
  const offset = 10 * scale;

  // Grid positions for South Indian chart (4x4 grid with center empty)
  // Signs are in fixed positions, starting from Pisces in top-left
  const gridPositions = [
    // Row 0
    { row: 0, col: 0, sign: 'Pisces' },
    { row: 0, col: 1, sign: 'Aries' },
    { row: 0, col: 2, sign: 'Taurus' },
    { row: 0, col: 3, sign: 'Gemini' },
    // Row 1
    { row: 1, col: 0, sign: 'Aquarius' },
    { row: 1, col: 3, sign: 'Cancer' },
    // Row 2
    { row: 2, col: 0, sign: 'Capricorn' },
    { row: 2, col: 3, sign: 'Leo' },
    // Row 3
    { row: 3, col: 0, sign: 'Sagittarius' },
    { row: 3, col: 1, sign: 'Scorpio' },
    { row: 3, col: 2, sign: 'Libra' },
    { row: 3, col: 3, sign: 'Virgo' }
  ];

  // Find which house a sign represents (based on ascendant)
  const getHouseFromSign = (sign) => {
    const ascIndex = signToIndex[ascendantSign] || 0;
    const signIndex = signToIndex[sign];
    if (signIndex === undefined) return null;
    const house = ((signIndex - ascIndex + 12) % 12) + 1;
    return house;
  };

  // Group planets by sign
  const planetsBySign = {};
  Object.entries(planets).forEach(([planet, data]) => {
    const sign = data.sign;
    if (sign) {
      if (!planetsBySign[sign]) planetsBySign[sign] = [];
      planetsBySign[sign].push({
        name: planet,
        degree: data.degree || 0,
        retrograde: data.retrograde || false
      });
    }
  });

  // Also group by house for fallback
  const planetsByHouse = {};
  Object.entries(planets).forEach(([planet, data]) => {
    const house = data.house;
    if (house) {
      if (!planetsByHouse[house]) planetsByHouse[house] = [];
      planetsByHouse[house].push({
        name: planet,
        degree: data.degree || 0,
        retrograde: data.retrograde || false
      });
    }
  });

  // Get planets for a sign
  const getPlanetsForSign = (sign) => {
    // First try by sign name
    if (planetsBySign[sign]) return planetsBySign[sign];
    
    // Fallback to house
    const house = getHouseFromSign(sign);
    if (house && planetsByHouse[house]) return planetsByHouse[house];
    
    return [];
  };

  return (
    <div className="inline-block">
      {title && (
        <div className="text-center text-amber-400 text-sm font-semibold mb-2">{title}</div>
      )}
      <svg 
        width={size} 
        height={size} 
        viewBox={`0 0 ${size} ${size}`}
        className="bg-gray-900/80 rounded-lg"
      >
        {/* Outer border */}
        <rect
          x={offset}
          y={offset}
          width={cellSize * 4}
          height={cellSize * 4}
          fill="none"
          stroke="#F59E0B"
          strokeWidth={2 * scale}
        />

        {/* Grid lines */}
        {[1, 2, 3].map(i => (
          <React.Fragment key={`grid-${i}`}>
            {/* Vertical lines */}
            <line
              x1={offset + i * cellSize}
              y1={offset}
              x2={offset + i * cellSize}
              y2={offset + cellSize * 4}
              stroke="#F59E0B"
              strokeWidth={1 * scale}
            />
            {/* Horizontal lines */}
            <line
              x1={offset}
              y1={offset + i * cellSize}
              x2={offset + cellSize * 4}
              y2={offset + i * cellSize}
              stroke="#F59E0B"
              strokeWidth={1 * scale}
            />
          </React.Fragment>
        ))}

        {/* Center diagonal lines for empty cells */}
        <line
          x1={offset + cellSize}
          y1={offset + cellSize}
          x2={offset + cellSize * 3}
          y2={offset + cellSize * 3}
          stroke="#F59E0B"
          strokeWidth={1 * scale}
          strokeDasharray="4,4"
        />
        <line
          x1={offset + cellSize * 3}
          y1={offset + cellSize}
          x2={offset + cellSize}
          y2={offset + cellSize * 3}
          stroke="#F59E0B"
          strokeWidth={1 * scale}
          strokeDasharray="4,4"
        />

        {/* Render each cell */}
        {gridPositions.map(({ row, col, sign }) => {
          const x = offset + col * cellSize;
          const y = offset + row * cellSize;
          const house = getHouseFromSign(sign);
          const isAscendant = sign === ascendantSign;
          const planetsInCell = getPlanetsForSign(sign);

          return (
            <g key={sign}>
              {/* Highlight ascendant cell */}
              {isAscendant && (
                <rect
                  x={x}
                  y={y}
                  width={cellSize}
                  height={cellSize}
                  fill="rgba(245, 158, 11, 0.1)"
                />
              )}

              {/* Sign abbreviation in top-left corner */}
              <text
                x={x + 8 * scale}
                y={y + 14 * scale}
                fill="#6B7280"
                fontSize={9 * scale}
                fontFamily="sans-serif"
              >
                {signAbbrev[sign]}
              </text>

              {/* House number in top-right corner */}
              {house && (
                <text
                  x={x + cellSize - 8 * scale}
                  y={y + 14 * scale}
                  fill="#4B5563"
                  fontSize={8 * scale}
                  fontFamily="sans-serif"
                  textAnchor="end"
                >
                  H{house}
                </text>
              )}

              {/* Ascendant marker */}
              {isAscendant && (
                <text
                  x={x + cellSize / 2}
                  y={y + 22 * scale}
                  fill="#F59E0B"
                  fontSize={8 * scale}
                  fontWeight="bold"
                  fontFamily="sans-serif"
                  textAnchor="middle"
                >
                  ASC
                </text>
              )}

              {/* Planets in cell */}
              {planetsInCell.map((planet, idx) => {
                const abbrev = planetAbbrev[planet.name] || planet.name.substring(0, 2);
                const color = planetColors[planet.name] || '#FFFFFF';
                const retro = planet.retrograde ? '(R)' : '';
                const degreeStr = showDegrees ? ` ${planet.degree?.toFixed(0)}°` : '';
                
                // Position planets in a grid within the cell
                const pRow = Math.floor(idx / 2);
                const pCol = idx % 2;
                const px = x + 20 * scale + pCol * 40 * scale;
                const py = y + 45 * scale + pRow * 16 * scale;

                return (
                  <text
                    key={`${sign}-${planet.name}`}
                    x={px}
                    y={py}
                    fill={color}
                    fontSize={11 * scale}
                    fontWeight="600"
                    fontFamily="monospace"
                  >
                    {abbrev}{retro}{degreeStr}
                  </text>
                );
              })}
            </g>
          );
        })}

        {/* Center label */}
        <text
          x={offset + cellSize * 2}
          y={offset + cellSize * 2}
          fill="#F59E0B"
          fontSize={12 * scale}
          fontWeight="bold"
          fontFamily="sans-serif"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          KUNDLI
        </text>
      </svg>
    </div>
  );
};

export default SouthIndianChart;
